import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Auth.css';

const getStrength = pw => {
  if (!pw) return { score: 0, label: '', cls: '' };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { label: '', cls: '' },
    { label: 'Weak',   cls: 'strength-weak' },
    { label: 'Fair',   cls: 'strength-fair' },
    { label: 'Good',   cls: 'strength-good' },
    { label: 'Strong', cls: 'strength-strong' },
    { label: 'Strong', cls: 'strength-strong' },
  ];
  return { score, ...map[score] };
};

const Rule = ({ met, text }) => (
  <li className={met ? 'met' : ''}>{text}</li>
);

const ResetPassword = () => {
  const { token }  = useParams();
  const navigate   = useNavigate();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading]   = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState('');

  const pw       = formData.password;
  const strength = getStrength(pw);
  const pctWidth = `${(strength.score / 5) * 100}%`;

  const handleChange = e =>
    setFormData(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (pw !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (pw.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/auth/reset-password/${token}`, {
        password:        pw,
        confirmPassword: formData.confirmPassword
      });
      setDone(true);
      toast.success('Password updated!');
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h1>Password updated</h1>
          <p className="auth-subtitle">
            Your password has been changed successfully. You can now sign in
            with your new password.
          </p>

          <div className="alert alert-success">
            ✓ Password updated successfully.
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/login/user')}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Choose a new password</h1>
        <p className="auth-subtitle">
          Make it at least 8 characters and ideally a mix of letters, numbers, and symbols.
        </p>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            ⚠ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={pw}
              onChange={handleChange}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              required
              autoFocus
            />
            {pw && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div
                    className={`strength-fill ${strength.cls}`}
                    style={{ width: pctWidth }}
                  />
                </div>
                <span className={`strength-label ${strength.cls}`}>
                  {strength.label}
                </span>
              </div>
            )}
            <ul className="password-rules">
              <Rule met={pw.length >= 8}       text="At least 8 characters" />
              <Rule met={/[A-Z]/.test(pw)}     text="One uppercase letter" />
              <Rule met={/[a-z]/.test(pw)}     text="One lowercase letter" />
              <Rule met={/[0-9]/.test(pw)}     text="One number" />
            </ul>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your new password"
              autoComplete="new-password"
              required
            />
            {formData.confirmPassword && pw !== formData.confirmPassword && (
              <small style={{ color: '#c53030' }}>Passwords do not match</small>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || pw !== formData.confirmPassword || pw.length < 8}
          >
            {loading ? 'Updating…' : 'Update Password'}
          </button>
        </form>

        <div className="auth-links">
          <p><Link to="/login/user">Back to sign in</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
